import { useMemo } from "react";
import cx from "classnames";

import { Tick } from "components/icons/generated";
import Icon from "ds/components/Icon";

import styles from "./styles.module.css";
import { CircularProgressProps } from "./types";
import { getDefaultAriaLabel } from "./utils";
import { CENTER, RADIUS, STROKE_WIDTH, VIEWBOX_SIZE } from "./constants";

const CircularProgress = ({
  state,
  progress = 0,
  className,
  ariaLabel,
  variant = "default",
}: CircularProgressProps) => {
  const circumference = 2 * Math.PI * RADIUS;

  const clampedProgress = Math.min(100, Math.max(0, progress));

  const strokeDashoffset = useMemo(
    () => circumference - (clampedProgress / 100) * circumference,
    [circumference, clampedProgress]
  );

  const defaultAriaLabel = getDefaultAriaLabel(state, clampedProgress);

  const ariaValueNow = useMemo(() => {
    if (state === "completed") return 100;
    if (state === "in-progress") return clampedProgress;
    return 0;
  }, [state, clampedProgress]);

  return (
    <div
      className={cx(styles.container, styles[state], styles[variant], className)}
      role="progressbar"
      aria-label={ariaLabel || defaultAriaLabel}
      aria-valuemin={0}
      aria-valuenow={ariaValueNow}
      aria-valuemax={100}
    >
      {state === "completed" ? (
        <div className={styles.completedState}>
          <Icon src={Tick} className={styles.completedStateIcon} />
        </div>
      ) : (
        <svg
          className={styles.svg}
          viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle
            className={styles.backgroundCircle}
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            strokeWidth={STROKE_WIDTH}
            fill="none"
          />

          {state === "in-progress" && (
            <circle
              className={styles.progressCircle}
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              strokeWidth={STROKE_WIDTH}
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              // Rotate to start from top (12 o'clock position)
              transform={`rotate(-90 ${CENTER} ${CENTER})`}
            />
          )}
        </svg>
      )}
    </div>
  );
};

CircularProgress.displayName = "DS.CircularProgress";

export default CircularProgress;
